import React, { useState, useEffect } from "react";
import { authService } from "../services/auth";
import {
  checkSessionActiveByLoginName,
  logoutByLoginName,
} from "../services/sessionApi";
import { ForgotPassword } from "./ForgotPassword";
import pabLoginLogo from "../images/pab-login-logo.webp";
import bg1 from "../images/bg_1.jpg";
import bg2 from "../images/bg_2.webp";
import bg3 from "../images/bg_3.webp";

interface LoginProps {
  onLogin: (encryptedData: string) => void;
}

// Background images for the slider
const bgImages = [
  bg1,
  bg2,
  bg3,
];

// Slide content for the marketing panel
const slides = [
  {
    badge: "v2.0 Update Live",
    title: (
      <>
        Dark Mode <br />& New UI.
      </>
    ),
    description:
      "Easy on the eyes, heavy on performance. Switch themes instantly to suit your workspace.",
  },
  {
    badge: "New Features",
    title: (
      <>
        Inventory <br />& Analytics.
      </>
    ),
    description:
      "Track parts in real-time and visualize your monthly revenue with our new dashboard widgets.",
  },
  {
    badge: "Coming Soon",
    title: (
      <>
        Mobile App <br />Integration.
      </>
    ),
    description:
      "Manage your garage on the go. Early access for the iOS and Android app starts next month.",
  },
];

export const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showEndSessionModal, setShowEndSessionModal] = useState(false);

  // Auto-rotate slides every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const doLogin = async () => {
    const response = await authService.login({
      loginName: username,
      password: password,
      ip: "",
      os: navigator.userAgent,
      deviceType:1
    });

    if (response.success) {
      localStorage.setItem("encryptedData", response.data);
      const decryptedData = authService.decryptData(response.data);
      if (decryptedData) {
        // Store session_id if present in decrypted data
        if (decryptedData.session_id) {
          localStorage.setItem("session_id", String(decryptedData.session_id));
        }
        
        try {
          const encryptedUserData = authService.encryptData(decryptedData);
          if (encryptedUserData) {
            localStorage.setItem("encryptedUserData", encryptedUserData);
            localStorage.removeItem("userData");
          }
        } catch {}
      }
      onLogin(response.data);
    } else {
      setError(response.message || "Login failed");
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !password) {
      setError("Please enter username and password");
      return;
    }
    setIsLoading(true);
    setError("");

    try {
      const sessionRes = await checkSessionActiveByLoginName(username.trim());
      if (sessionRes?.success && sessionRes.active) {
        setShowEndSessionModal(true);
        return;
      }
      await doLogin();
    } catch (error) {
      console.error("Login error:", error);
      setError("Login failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmEndSessionAndLogin = async () => {
    setShowEndSessionModal(false);
    setIsLoading(true);
    setError("");
    try {
      await logoutByLoginName(username.trim());
      await doLogin();
    } catch (err) {
      setError("Failed to end session or login. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const togglePassword = () => {
    setShowPassword(!showPassword);
  };

  // Icon Components
  const UserIcon = () => (
    <svg
      className="w-5 h-5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
      />
    </svg>
  );

  const LockIcon = () => (
    <svg
      className="w-5 h-5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
      />
    </svg>
  );

  const EyeIcon = () => (
    <svg
      className="w-5 h-5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
      />
    </svg>
  );

  const EyeSlashIcon = () => (
    <svg
      className="w-5 h-5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
      />
    </svg>
  );

  const LinkedInIcon = () => (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );

  const YouTubeIcon = () => (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
    </svg>
  );

  const GlobeIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-9 3-9s-1.343-9-3-9m0 18c-1.657 0-3-9-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
    </svg>
  );

  const ArrowRightIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
    </svg>
  );

  const ArrowLeftIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
    </svg>
  );

  return (
    <div className="h-screen w-full flex overflow-hidden font-sans bg-white">
      {/* Left Panel - Brand Panel with Background Image Slider */}
      <div className="hidden lg:flex flex-1 flex-col justify-center p-16 text-white relative overflow-hidden">
        {/* Background Image Slider */}
        <div className="absolute inset-0 z-0">
          {bgImages.map((img, index) => (
            <div
              key={index}
              className={`absolute inset-0 bg-cover bg-center transition-opacity duration-[1500ms] ease-in-out scale-105 ${currentSlide === index ? "opacity-100" : "opacity-0"
                }`}
              style={{ backgroundImage: `url('${img}')` }}
            />
          ))}
        </div>

        {/* Gradient Overlay */}
        <div
          className="absolute inset-0 z-[1]"
          style={{
            background: "linear-gradient(135deg, rgba(34, 197, 94, 0.85), rgba(22, 163, 74, 0.9))",
          }}
        />

        {/* Animated Floating Shapes */}
        <div
          className="absolute w-[300px] h-[300px] -top-[50px] -left-[50px] bg-white/10 rounded-full z-[2] pointer-events-none"
          style={{
            animation: "float 18s infinite linear",
          }}
        />
        <div
          className="absolute w-[150px] h-[150px] bottom-[10%] right-[10%] bg-white/10 rounded-full z-[2] pointer-events-none"
          style={{
            animation: "float 12s infinite linear",
            animationDelay: "-5s",
          }}
        />
        <div
          className="absolute w-[80px] h-[80px] top-[40%] left-[20%] bg-white/5 rounded-full z-[2] pointer-events-none"
          style={{
            animation: "float 20s infinite linear",
            animationDelay: "-2s",
          }}
        />

        {/* Content */}
        <div className="relative z-[3] h-[300px] flex flex-col justify-center">
          {/* Carousel Dots - Above Content */}
          <div className="flex gap-2 mb-6">
            {slides.map((_, index) => (
              <button
                key={index}
                className={`h-1 rounded transition-all duration-300 ${currentSlide === index
                  ? "bg-white w-9"
                  : "bg-white/30 w-6 hover:bg-white/50"
                  }`}
                onClick={() => setCurrentSlide(index)}
              />
            ))}
          </div>

          {/* Slides */}
          {slides.map((slide, index) => (
            <div
              key={index}
              className={`transition-all duration-500 ${currentSlide === index
                ? "block animate-fadeUp"
                : "hidden"
                }`}
            >
              <span className="inline-block bg-white/20 px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide mb-6">
                {slide.badge}
              </span>
              <h1 className="text-5xl font-bold leading-[1.1] mb-4">
                {slide.title}
              </h1>
              <p className="text-lg opacity-90 max-w-[90%] leading-relaxed">
                {slide.description}
              </p>
            </div>
          ))}

          {/* Social Links */}
          <div className="flex gap-3 mt-8">
            <a
              href="https://www.linkedin.com/showcase/techmgarage360/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-white/15 hover:bg-white/25 px-5 py-2.5 rounded-lg text-sm text-white transition-all"
            >
              <LinkedInIcon />
              <span>LinkedIn</span>
            </a>
            <a
              href="https://techmindsoftware.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-white/15 hover:bg-white/25 px-5 py-2.5 rounded-lg text-sm text-white transition-all"
            >
              <GlobeIcon />
              <span>Website</span>
            </a>
            <a
              href="https://garage360.app/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-white/15 hover:bg-white/25 px-5 py-2.5 rounded-lg text-sm text-white transition-all"
            >
              <GlobeIcon />
              <span>Product</span>
            </a>

          </div>
        </div>

        {/* Footer */}
        <div className="absolute bottom-8 left-16 z-[3] text-sm opacity-70">
          © 2026 Techmind Solutions. All rights reserved.
        </div>
      </div>

      {/* Right Panel - Login Form or Forgot Password */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white relative">
        {showForgotPassword ? (
          <ForgotPassword
            onBackToLogin={() => {
              setShowForgotPassword(false);
              setError("");
            }}
          />
        ) : (
          <div className="w-full max-w-[420px]">
            {/* Logo */}
            <div className="mb-8">
              <img src={pabLoginLogo} alt="Garage360 Logo" className="h-12 w-auto" />
            </div>

            {/* Welcome Header */}
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-2">Welcome back!</h2>
              <p className="text-gray-500">Log in to manage your garage efficiently.</p>
            </div>

            {/* Login Form */}
            <form onSubmit={handleLogin}>
              {/* Username Field */}
              <div className="mb-5">
                <label
                  htmlFor="username"
                  className="block text-sm font-medium text-gray-800 mb-2"
                >
                  Username
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                    <UserIcon />
                  </div>
                  <input
                    type="text"
                    id="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="w-full py-3 pl-11 pr-4 border border-gray-200 rounded-lg bg-gray-50 text-gray-800 placeholder-gray-400 focus:outline-none focus:border-green-500 focus:bg-white focus:ring-[3px] focus:ring-green-500/10 transition-all text-[0.95rem]"
                    placeholder="Enter your username"
                    required
                  />
                </div>
              </div>

              {/* Password Field */}
              <div className="mb-5">
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-800 mb-2"
                >
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                    <LockIcon />
                  </div>
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full py-3 pl-11 pr-11 border border-gray-200 rounded-lg bg-gray-50 text-gray-800 placeholder-gray-400 focus:outline-none focus:border-green-500 focus:bg-white focus:ring-[3px] focus:ring-green-500/10 transition-all text-[0.95rem]"
                    placeholder="Enter your password"
                    required
                  />
                  <button
                    type="button"
                    onClick={togglePassword}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 cursor-pointer"
                  >
                    {showPassword ? <EyeIcon /> : <EyeSlashIcon />}
                  </button>
                </div>
              </div>

              {/* Remember Me & Forgot Password */}
              <div className="flex items-center justify-between mb-8 text-sm">
                <label className="flex items-center gap-2 text-gray-500 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="w-4 h-4 accent-green-500 rounded"
                  />
                  Remember me
                </label>
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-green-600 font-medium hover:underline"
                >
                  Forgot password?
                </button>
              </div>

              {/* Error Message */}
              {error && (
                <div className="text-red-600 bg-red-50 border border-red-200 rounded-lg p-3 mb-5 text-sm">
                  {error}
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className={`w-full py-3.5 px-4 bg-gray-900 hover:bg-black text-white font-semibold rounded-lg flex items-center justify-center gap-2 transition-all hover:-translate-y-px ${isLoading ? "opacity-70 cursor-not-allowed" : ""
                  }`}
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-transparent border-t-current rounded-full animate-spin" />
                    Logging in...
                  </>
                ) : (
                  <>
                    Log in
                    <ArrowRightIcon />
                  </>
                )}
              </button>
            </form>

            {/* End session and login modal */}
            {showEndSessionModal && (
              <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
                <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
                  <div className="bg-red-600 px-5 py-4 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-white">
                      End session
                    </h3>
                    <button
                      type="button"
                      onClick={() => setShowEndSessionModal(false)}
                      className="p-1.5 rounded-lg bg-white/20 hover:bg-white/30 text-white transition-colors"
                      aria-label="Close"
                    >
                      <span className="text-lg leading-none">×</span>
                    </button>
                  </div>
                  <div className="p-5 text-gray-700">
                    <p className="mb-2">
                      This user is already logged in. Do you want to end the
                      existing session and sign in?
                    </p>
                    <p className="text-sm text-gray-500">
                      This action cannot be undone.
                    </p>
                  </div>
                  <div className="flex gap-3 justify-end px-5 pb-5">
                    <button
                      type="button"
                      onClick={() => setShowEndSessionModal(false)}
                      className="px-4 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleConfirmEndSessionAndLogin}
                      className="px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors"
                    >
                      Yes, end session and login
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="text-center mt-8 text-sm text-gray-400">
              Powered by{' '}
              <a
                href="https://techmindsoftware.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-green-600 font-bold transition-colors"
              >
                Techmind Software
              </a>{' '}
              •{' '}
              <a
                href="https://garage360.app/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-green-600 font-bold transition-colors"
              >
                Garage360
              </a>{' '}
              • Secure Connection
            </div>
          </div>
        )}
      </div>

      {/* CSS Keyframes for float animation */}
      <style>{`
        @keyframes float {
          0% { transform: translate(0, 0) rotate(0deg); }
          33% { transform: translate(30px, -50px) rotate(10deg); }
          66% { transform: translate(-20px, 20px) rotate(-5deg); }
          100% { transform: translate(0, 0) rotate(0deg); }
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(15px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeUp {
          animation: fadeUp 0.8s ease-out;
        }
      `}</style>
    </div>
  );
};
